import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Center, Container, Group, Paper, Select, SimpleGrid, Stack, Text, Title } from "@mantine/core"
import { IconAlertTriangle, IconCoin, IconReportMoney } from "@tabler/icons-react"
import { KpiRowSkeleton, TableSkeleton } from "../../../../components/PageSkeletons"
import type { IPlatformOverviewTotals } from "../../../../lib/api/platform-billing"
import { formatInteger } from "../../../../lib/format-date"
import type { TDateRange } from "../../-usage/date-range"
import { formatPct, formatUsd, profitTone } from "../format"
import { PlatformTenantTable } from "../PlatformTenantTable"
import type { IProps, TKpiTone } from "./interfaces"
import classes from "./styles.module.css"

interface IKpiTileProps {
  label: React.ReactNode
  value: string
  sublabel?: React.ReactNode
  tone?: TKpiTone
}

function KpiTile({ label, value, sublabel, tone = "neutral" }: IKpiTileProps): React.JSX.Element {
  return (
    <Paper withBorder radius="md" p="md" className={classes.tile} data-tone={tone}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text className={classes.tileValue} data-tone={tone}>
        {value}
      </Text>
      {sublabel ? (
        <Text size="xs" c="dimmed" mt={2}>
          {sublabel}
        </Text>
      ) : null}
    </Paper>
  )
}

/** Overall margin percentage — undefined (em dash) when there was no revenue. */
function overallMarginPct(totals: IPlatformOverviewTotals): number {
  return totals.billableUsd > 0 ? (totals.marginUsd / totals.billableUsd) * 100 : Number.NaN
}

function toKpiTone(tone: ReturnType<typeof profitTone>): TKpiTone {
  if (tone === "profit") return "success"
  if (tone === "loss") return "danger"
  return "neutral"
}

export const PlatformBillingView = ({
  overview,
  isLoading,
  isError,
  range,
  onRangeChange,
}: IProps): React.JSX.Element => {
  // 2. EXTERNAL HOOKS
  const { i18n } = useLingui()

  // 7. RENDER
  const rangeSelect = (
    <Select
      value={range}
      onChange={(v) => v && onRangeChange(v as TDateRange)}
      data={[
        { value: "mtd", label: i18n._(msg`Month to date`) },
        { value: "7d", label: i18n._(msg`Last 7 days`) },
        { value: "30d", label: i18n._(msg`Last 30 days`) },
        { value: "90d", label: i18n._(msg`Last 90 days`) },
      ]}
      w={170}
      aria-label={i18n._(msg`Billing period`)}
    />
  )

  const header = (
    <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
      <div>
        <Title order={2}>
          <Trans>Platform billing</Trans>
        </Title>
        <Text c="dimmed" size="sm">
          <Trans>
            Cost, revenue and profit across all tenants, plus the outstanding credit liability. Money figures follow the
            selected period; credit figures are lifetime.
          </Trans>
        </Text>
      </div>
      {rangeSelect}
    </Group>
  )

  if (isError) {
    return (
      <Container size="xl" py="md" className={classes.page}>
        <Stack gap="lg">
          {header}
          <Alert color="danger" icon={<IconAlertTriangle size={16} />}>
            <Trans>Couldn’t load billing data. Try a different period or refresh the page.</Trans>
          </Alert>
        </Stack>
      </Container>
    )
  }

  if (isLoading && !overview) {
    return (
      <Container size="xl" py="md" className={classes.page}>
        <Stack gap="lg">
          {header}
          <KpiRowSkeleton count={4} />
          <TableSkeleton />
        </Stack>
      </Container>
    )
  }

  const totals = overview?.totals ?? null
  const perTenant = overview?.perTenant ?? []
  const marginTone = totals ? toKpiTone(profitTone(totals.marginUsd)) : "neutral"

  return (
    <Container size="xl" py="md" className={classes.page}>
      <Stack gap="lg">
        {header}

        {totals ? (
          <Stack gap="md">
            {/* Money — date-windowed by the selected period. */}
            <div>
              <Group gap={6} mb="xs" align="center">
                <IconReportMoney size={16} className={classes.sectionIcon} aria-hidden />
                <Text size="sm" fw={700} tt="uppercase">
                  <Trans>This period</Trans>
                </Text>
                <Text size="xs" c="dimmed">
                  <Trans>money · follows the selected period</Trans>
                </Text>
              </Group>
              <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
                <KpiTile label={<Trans>COGS</Trans>} value={formatUsd(totals.platformCostUsd)} tone="info" />
                <KpiTile label={<Trans>Revenue (billed)</Trans>} value={formatUsd(totals.billableUsd)} tone="brand" />
                <KpiTile
                  label={<Trans>Profit</Trans>}
                  value={formatUsd(totals.marginUsd)}
                  sublabel={<Trans>{formatPct(overallMarginPct(totals))} margin</Trans>}
                  tone={marginTone}
                />
                <KpiTile
                  label={<Trans>BYOK notional</Trans>}
                  value={formatUsd(totals.byokNotionalUsd)}
                  sublabel={<Trans>customer-paid, not our COGS</Trans>}
                  tone="neutral"
                />
              </SimpleGrid>
            </div>

            {/* Credits — lifetime / cumulative, NOT windowed. */}
            <div>
              <Group gap={6} mb="xs" align="center">
                <IconCoin size={16} className={classes.sectionIcon} aria-hidden />
                <Text size="sm" fw={700} tt="uppercase">
                  <Trans>Lifetime credits</Trans>
                </Text>
                <Text size="xs" c="dimmed">
                  <Trans>cumulative · a balance is a snapshot, not a period sum</Trans>
                </Text>
              </Group>
              <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
                <KpiTile label={<Trans>Granted</Trans>} value={formatInteger(i18n, totals.creditsGranted)} />
                <KpiTile label={<Trans>Purchased</Trans>} value={formatInteger(i18n, totals.creditsPurchased)} />
                <KpiTile label={<Trans>Spent</Trans>} value={formatInteger(i18n, totals.creditsSpent)} />
                <KpiTile
                  label={<Trans>Outstanding balance</Trans>}
                  value={formatInteger(i18n, totals.creditBalance)}
                  sublabel={<Trans>≈ {formatUsd(totals.creditBalanceUsd)} liability</Trans>}
                  tone="info"
                />
              </SimpleGrid>
            </div>
          </Stack>
        ) : null}

        <div>
          <Group justify="space-between" align="flex-end" mb="xs" wrap="wrap">
            <Title order={4}>
              <Trans>Per tenant</Trans>
            </Title>
            <Text size="xs" c="dimmed">
              <Trans>Money columns follow the period; credit columns are lifetime.</Trans>
            </Text>
          </Group>

          {perTenant.length === 0 ? (
            <Center mih={160}>
              <Text c="dimmed">
                <Trans>No tenant billing activity yet.</Trans>
              </Text>
            </Center>
          ) : (
            <PlatformTenantTable rows={perTenant} />
          )}
        </div>
      </Stack>
    </Container>
  )
}
